import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Clock, MapPin, Calendar, Users, ExternalLink, ChevronRight } from 'lucide-react';
import api from '../services/api';
import { formatAgeRequirement } from '../utils/eventLabels';

const UpcomingEvents = ({ limit = 4, showHeader = true, className = "" }) => {
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchUpcomingEvents();
    }, []);

    const fetchUpcomingEvents = async () => {
        try {
            setLoading(true);
            // Get upcoming events (public endpoint)
            const response = await api.get(`/events/upcoming?limit=${limit}`);
            if (response.data.success) {
                setEvents(response.data.data || []);
            } else {
                setError('Failed to load upcoming events');
            }
        } catch (err) {
            console.error('Error fetching upcoming events:', err);
            setError('Failed to load upcoming events');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            year: 'numeric'
        });
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    const getPriceLabel = (price) => {
        if (typeof price === 'string' && price.trim().toLowerCase() === 'free') return 'Free';
        if (!price || parseFloat(price) === 0) return 'Free';
        return `$${price}`;
    };

    const getEventTypeColor = (type) => {
        const colors = {
            'concert': 'text-purple-400 bg-purple-500/20',
            'festival': 'text-pink-400 bg-pink-500/20',
            'livestream': 'text-blue-400 bg-blue-500/20',
            'workshop': 'text-green-400 bg-green-500/20',
            'meetup': 'text-yellow-400 bg-yellow-500/20',
            'release': 'text-red-400 bg-red-500/20'
        };
        return colors[type] || 'text-gray-400 bg-gray-500/20';
    };

    const reservedHeight = limit * 212;

    if (loading) {
        return (
            <div className={`upcoming-events-widget ${className}`} style={{ minHeight: `${reservedHeight}px` }}>
                {showHeader && (
                    <div className="flex items-center gap-3 mb-6">
                        <Clock className="w-6 h-6 text-yellow-400" />
                        <h2 className="text-xl font-bold text-white">Upcoming Events</h2>
                    </div>
                )}

                <div className="space-y-4">
                    {[...Array(limit)].map((_, index) => (
                        <div key={index} className="bg-gray-800/30 rounded-lg p-4 border border-gray-700/50 animate-pulse min-h-[200px]">
                            <div className="h-4 bg-gray-700 rounded w-3/4 mb-2"></div>
                            <div className="h-3 bg-gray-700 rounded w-1/2 mb-2"></div>
                            <div className="h-3 bg-gray-700 rounded w-1/4"></div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`upcoming-events-widget ${className}`} style={{ minHeight: `${reservedHeight}px` }}>
                {showHeader && (
                    <div className="flex items-center gap-3 mb-6">
                        <Clock className="w-6 h-6 text-yellow-400" />
                        <h2 className="text-xl font-bold text-white">Upcoming Events</h2>
                    </div>
                )}

                <div className="text-center py-8">
                    <div className="text-4xl mb-2">📅</div>
                    <p className="text-gray-400">{error}</p>
                </div>
            </div>
        );
    }

    if (events.length === 0) {
        return (
            <div className={`upcoming-events-widget ${className}`} style={{ minHeight: `${reservedHeight}px` }}>
                {showHeader && (
                    <div className="flex items-center gap-3 mb-6">
                        <Clock className="w-6 h-6 text-yellow-400" />
                        <h2 className="text-xl font-bold text-white">Upcoming Events</h2>
                    </div>
                )}

                <div className="text-center py-8">
                    <div className="text-4xl mb-2">🎭</div>
                    <p className="text-gray-400 mb-4">No upcoming events scheduled</p>
                    <p className="text-sm text-gray-500">Check back soon for new events!</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`upcoming-events-widget ${className} w-full max-w-full`}>
            {showHeader && (
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                    <div className="flex items-center gap-3">
                        <Clock className="w-6 h-6 text-yellow-400" />
                        <h2 className="text-xl font-bold text-white">Upcoming Events</h2>
                    </div>
                    <a 
                        href="/calendar" 
                        className="text-sm text-yellow-400 hover:text-yellow-300 active:text-yellow-200 flex items-center gap-1 transition-colors self-center sm:self-auto touch-manipulation"
                    >
                        View All
                        <ChevronRight className="w-4 h-4" />
                    </a>
                </div>
            )}
            
            <div className="space-y-4 w-full max-w-full">
                {events.map((event) => (
                    <div
                        key={event.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate(`/events/${event.slug || event.id}`)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                navigate(`/events/${event.slug || event.id}`);
                            }
                        }}
                        className="relative rounded-lg p-3 sm:p-4 border border-gray-700/50 hover:border-yellow-500/30 active:border-yellow-500/50 transition-all duration-300 group touch-manipulation min-h-[200px] w-full max-w-full cursor-pointer"
                        style={{
                            backgroundImage: event.cover_image_url ? `url(${event.cover_image_url})` : 'none',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundColor: event.cover_image_url ? 'transparent' : 'rgba(31, 41, 55, 0.3)'
                        }}
                    >
                        {/* Background overlay for better text readability */}
                        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-all duration-300"></div>

                        {/* Date badge, pinned to the top-right corner */}
                        <div className="absolute top-3 right-3 sm:top-4 sm:right-4 z-20 flex items-center gap-1.5 bg-black/70 px-3 py-1.5 rounded-lg border border-green-500/40">
                            <Calendar className="w-4 h-4 text-green-400" />
                            <span className="text-sm font-extrabold text-green-400 tracking-wide whitespace-nowrap">
                                {formatDate(event.start_date)}
                                <span className="italic font-black text-green-300">!</span>
                            </span>
                        </div>

                        {/* Content container */}
                        <div className="relative z-10 w-full max-w-full">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-3 w-full">
                            <div className="flex-1 min-w-0 w-full max-w-full pr-20 sm:pr-28">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2 w-full">
                                    <h3 className="inline-block self-start sm:self-center font-semibold text-white group-hover:text-yellow-300 transition-colors bg-black/70 px-2 py-1 rounded truncate max-w-full">
                                        {event.title}
                                    </h3>
                                    <div className="flex items-center gap-2">
                                        <span className={`text-xs px-2 py-1 rounded-full flex-shrink-0 ${getEventTypeColor(event.event_type)}`}>
                                            {event.event_type}
                                        </span>
                                    </div>
                                </div>
                                
                                {event.venue_name && (
                                    <p className="text-sm text-gray-200 inline-flex items-center gap-1 mb-2 bg-black/70 px-2 py-1 rounded max-w-full">
                                        <MapPin className="w-3 h-3 flex-shrink-0" />
                                        <span className="truncate max-w-full min-w-0 break-words">{event.venue_name}{event.city && `, ${event.city}`}</span>
                                    </p>
                                )}
                                
                                <div className="flex flex-wrap gap-x-2 gap-y-2 text-xs text-gray-300 w-full max-w-full">
                                    <span className="flex items-center gap-1.5 bg-black/70 px-3 py-1.5 rounded-lg border border-blue-500/40">
                                        <Clock className="w-4 h-4 text-blue-400" />
                                        <span className="text-sm font-extrabold text-blue-400 tracking-wide whitespace-nowrap">
                                            {formatTime(event.start_date)}
                                            {event.end_date && ` - ${formatTime(event.end_date)}`}
                                        </span>
                                    </span>
                                    {event.capacity > 0 && (
                                        <span className="flex items-center gap-1 bg-black/70 px-2 py-1 rounded">
                                            <Users className="w-3 h-3" />
                                            {event.capacity} capacity
                                        </span>
                                    )}
                                    {event.attendee_count > 0 && (
                                        <span className="flex items-center gap-1 bg-black/70 px-2 py-1 rounded">
                                            <Users className="w-3 h-3" />
                                            {event.attendee_count} attending
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="mb-3 h-[4.5rem]"></div>
                        
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 w-full max-w-full">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-medium text-green-400 bg-black/70 px-2 py-1 rounded">
                                    {getPriceLabel(event.ticket_price)}
                                </span>
                                {event.age_requirement && (
                                    <span className="text-xs text-gray-300 bg-black/70 px-2 py-1 rounded">
                                        {formatAgeRequirement(event.age_requirement)}
                                    </span>
                                )}
                            </div>
                            
                            <div className="flex items-center gap-2 justify-center sm:justify-end flex-wrap">
                                {event.ticket_url && (
                                    <a
                                        href={event.ticket_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="text-xs bg-yellow-600/10 hover:bg-yellow-600/20 active:bg-yellow-600/30 text-yellow-400 px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1 touch-manipulation min-h-[36px]"
                                    >
                                        <ExternalLink className="w-3 h-3" />
                                        Tickets
                                    </a>
                                )}
                                <Link
                                    to={`/events/${event.slug || event.id}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-xs bg-black/80 hover:bg-black/90 active:bg-black text-yellow-400 px-3 py-2 rounded-lg transition-all duration-200 touch-manipulation min-h-[36px] flex items-center"
                                >
                                    View Details
                                </Link>
                            </div>
                        </div>
                        </div>
                        
                    </div>
                ))}
            </div>
            
            {events.length >= limit && (
                <div className="mt-6 text-center">
                    <a
                        href="/calendar"
                        className="inline-flex items-center gap-2 text-sm text-yellow-400 hover:text-yellow-300 active:text-yellow-200 transition-colors touch-manipulation min-h-[44px]"
                    >
                        View All Upcoming Events
                        <ChevronRight className="w-4 h-4" />
                    </a>
                </div>
            )}
        </div>
    );
};

export default UpcomingEvents;